import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import i18next from '../../lang';
import {getTimeSlots} from "../purchase/DeliveryDateTime";
import {getStatusColor, getStatusStyle, getStatusText} from './OrderManagement';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {rootStore} from '../../stores/RootStore';

export interface OrderClassroomProduct {
  orderClassroomId: number;
  productType: string;
  productId: number;
  productTitle: string;
  productPrice: number;
  quantity: number;
}

export interface OrderClassRoom {
  id: number;
  orderId: number;
  classroomName: string;
  userId: string;
  address: string;
  deliveryDate: string;
  deliveryTime: number;
  postage: number;
  tax: number;
  trackingNumber: string;
  orderNumber: string;
  status: string;
  invoiceId: number | null;
  shippingCompletedAt: string | null;
  products: OrderClassroomProduct[];
}

export interface OrderDetailData {
  id: string;
  requestCompany: string;
  requesterId: string;
  totalAmount: number;
  status: string;
  mixed: boolean;
  createdAt: string;
  classrooms: OrderClassRoom[];
}

const OrderDetail: React.FC = () => {
  const { id } = useParams();
  const { authStore, viewStatusStore } = rootStore;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [order, setOrder] = useState<OrderDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [classroomStatusDialogOpen, setClassroomStatusDialogOpen] = useState(false);
  const [selectedClassroomStatus, setSelectedClassroomStatus] = useState('');
  const [selectedClassroom, setSelectedClassroom] = useState<OrderClassRoom | null>(null);
  const [shippingCompletedDate, setShippingCompletedDate] = useState('');
  const [orderNumberInput, setOrderNumberInput] = useState('');
  const [orderNumberError, setOrderNumberError] = useState('');

  // ステータス変更ダイアログを開く（配送完了日のデフォルト値を設定）
  const openClassroomStatusDialog = (classroom: OrderClassRoom) => {
    setSelectedClassroomStatus(classroom.status);
    setSelectedClassroom(classroom);
    // 既に配送完了日が設定済みならその日付、なければ今日の日付をデフォルトに
    if (classroom.shippingCompletedAt) {
      setShippingCompletedDate(classroom.shippingCompletedAt.substring(0, 10));
    } else {
      setShippingCompletedDate(new Date().toISOString().substring(0, 10));
    }
    setOrderNumberInput(classroom.orderNumber || '');
    setOrderNumberError('');
    setClassroomStatusDialogOpen(true);
  };

  const [editingTrackingNumber, setEditingTrackingNumber] = useState<number | null>(null);
  const [editTrackingNumberValue, setEditTrackingNumberValue] = useState('');

  useEffect(() => {
    fetchOrderDetail();
  }, []);

  const fetchOrderDetail = async () => {
    try {
      const response = await axios.get(`/api/v1/orders/${id}`);
      setOrder(response.data);
      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch order detail:', error);
      const status = error?.response?.status;
      if (status === 403 || status === 404 || status === 500) {
        setError(i18next.t('common.page_not_accessible'));
      } else {
        setError(i18next.t('common.page_not_accessible'));
      }
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return i18next.t('common.unspecified');
    const date = new Date(dateString + 'Z');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleStatusChange = async () => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      await axios.put(`/api/v1/orders/${id}`, {id: order.id, status: selectedStatus});
      await fetchOrderDetail();
      setStatusDialogOpen(false);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  // const getStatusOptions = () => {
  //   let statusOptions = [{ value: 'REQUEST', label: i18next.t('purchase.order_request') }];
  //
  //   if (authStore.user.level === 'ADMIN') {
  //     statusOptions.push({ value: 'ACCEPTANCE', label: i18next.t('order.order_received') });
  //     statusOptions.push({ value: 'REJECT', label: i18next.t('order.order_rejected') });
  //   } else {
  //     statusOptions.push({ value: 'CANCEL', label: i18next.t('order.order_cancelled') });
  //   }
  //   return statusOptions;
  // };

  const handleClassroomStatusChange = async () => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      if (selectedClassroom) {
        if (selectedClassroomStatus === 'SHIPPING_START') {
          if (!orderNumberInput.trim()) {
            setOrderNumberError(i18next.t('order.delivery_slip_number_required'));
            return;
          }
          if (!/^\d{5,6}$/.test(orderNumberInput)) {
            setOrderNumberError(i18next.t('order.delivery_slip_number_format'));
            return;
          }
        }
        const updatedClassroom: any = {
          ...selectedClassroom,
          status: selectedClassroomStatus
        };
        if (selectedClassroomStatus === 'SHIPPING_START') {
          updatedClassroom.orderNumber = orderNumberInput;
        }
        // 配送完了日が手動指定されている場合、UTC 00:00:00 としてリクエストに含める
        if (selectedClassroomStatus === 'SHIPPING_COMPLETE' && shippingCompletedDate) {
          const [year, month, day] = shippingCompletedDate.split('-');
          const utcDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
          updatedClassroom.shippingCompletedAt = utcDate.toISOString();
        }

        await axios.put(`/api/v1/orders/${id}/classrooms/${selectedClassroom.id}`, updatedClassroom);
        await fetchOrderDetail();
      }
      setClassroomStatusDialogOpen(false);
      setShippingCompletedDate('');
    } catch (error) {
      console.error('Failed to update classroom status:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const getClassroomStatusOptions = () => {
    return [{value: 'CONFIRM', label: i18next.t('order.order_confirmed')},
      {value: 'SHIPPING_START', label: i18next.t('order.shipping_started')},
      {value: 'SHIPPING_COMPLETE', label: i18next.t('order.shipping_completed')}];
  };

  const handleStartEditTrackingNumber = (classroom: OrderClassRoom) => {
    setEditingTrackingNumber(classroom.id);
    setEditTrackingNumberValue(classroom.trackingNumber || '');
  };

  const handleSaveTrackingNumber = async (classroom: OrderClassRoom) => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      await axios.put(`/api/v1/orders/${id}/classrooms/${classroom.id}`, {
        ...classroom,
        trackingNumber: editTrackingNumberValue
      });
      await fetchOrderDetail();
      setEditingTrackingNumber(null);
      setEditTrackingNumberValue('');
    } catch (error) {
      console.error('Failed to update tracking number:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleCancelEditTrackingNumber = () => {
    setEditingTrackingNumber(null);
    setEditTrackingNumberValue('');
  };

  if (error) return (
    <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h6" color="error" gutterBottom>{error}</Typography>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
        {i18next.t('nav.order_status')}
      </Button>
    </Container>
  );
  if (!order) return <Typography>{i18next.t('common.page_not_accessible')}</Typography>;

  const getTimeSlot = (timeSlotId: number | null) => {
    if (!timeSlotId) return i18next.t('common.unspecified');
    const slot = getTimeSlots().find(slot => slot.id === timeSlotId);
    return slot ? slot.time : '';
  };

  const getSubtotal = (products: OrderClassroomProduct[]) => {
    return products.reduce((sum, product) => sum + product.quantity * product.productPrice, 0)
  };

  // モバイル用教室カードレンダリング
  const renderMobileClassroom = (classroom: OrderClassRoom, index: number) => (
    <Card key={classroom.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* 教室名 */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          #{index + 1} {classroom.classroomName}
        </Typography>

        {/* 教室情報 */}
        {[
          { label: i18next.t('address.address'), value: classroom.address },
          { label: i18next.t('order.delivery_date'), value: formatDate(classroom.deliveryDate) },
          { label: i18next.t('order.time_slot'), value: getTimeSlot(classroom.deliveryTime) },
        ].map((item, i) => (
          <React.Fragment key={item.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, minHeight: 32 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mr: 2, fontSize: '0.8rem' }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'right', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                {item.value}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.25 }} />
          </React.Fragment>
        ))}

        {/* 状態 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, minHeight: 32 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mr: 2, fontSize: '0.8rem' }}>
            {i18next.t('common.status')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={getStatusText(classroom.status)} sx={getStatusStyle(classroom.status)} size="small" />
            {(authStore.user.level === 'ADMIN' && !classroom.invoiceId) &&
              <IconButton size="small" onClick={() => openClassroomStatusDialog(classroom)} sx={{ padding: '4px' }}>
                <EditIcon fontSize="small" style={{ width: 14 }} />
              </IconButton>
            }
          </Box>
        </Box>
        <Divider sx={{ my: 0.25 }} />

        {/* 送り状番号 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, minHeight: 32 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mr: 2, fontSize: '0.8rem' }}>
            {i18next.t('order.tracking_number')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {editingTrackingNumber === classroom.id ? (
              <>
                <TextField size="small" value={editTrackingNumberValue} onChange={(e) => setEditTrackingNumberValue(e.target.value)}
                  sx={{ mr: 0.5, '& .MuiInputBase-root': { height: '32px', fontSize: '0.8rem' }, width: '140px' }} />
                <IconButton size="small" onClick={handleCancelEditTrackingNumber} sx={{ padding: '4px' }}>
                  <CloseIcon fontSize="small" style={{ width: 14 }} />
                </IconButton>
                <IconButton size="small" onClick={() => handleSaveTrackingNumber(classroom)} sx={{ padding: '4px' }}>
                  <SaveIcon fontSize="small" style={{ width: 14 }} />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{classroom.trackingNumber}</Typography>
                {authStore.user.level === 'ADMIN' && ['CONFIRM', 'SHIPPING_START'].includes(classroom.status) &&
                  <IconButton
                    size="small"
                    disabled={classroom.status === 'CONFIRM'}
                    onClick={() => handleStartEditTrackingNumber(classroom)}
                    sx={{ padding: '4px', ml: 0.5, opacity: classroom.status === 'CONFIRM' ? 0.3 : 1 }}
                  >
                    <EditIcon fontSize="small" style={{ width: 14 }} />
                  </IconButton>
                }
              </>
            )}
          </Box>
        </Box>

        {/* 商品リスト */}
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, fontSize: '0.8rem' }}>
          {i18next.t('order.textbook_name')}
        </Typography>
        {classroom.products.map((product, pIndex) => (
          <Box key={`${product.orderClassroomId}-${product.productType}-${product.productId}`} sx={{ mb: 1, pl: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              {pIndex + 1}. {product.productTitle}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {i18next.t('common.price_yen', { price: product.productPrice })} × {product.quantity}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                {i18next.t('common.price_yen', { price: product.productPrice * product.quantity })}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* 合計 */}
        <Divider sx={{ my: 1 }} />
        {[
          { label: i18next.t('order.subtotal'), value: getSubtotal(classroom.products) },
          { label: i18next.t('user.shipping_fee'), value: classroom.postage },
          { label: i18next.t('order.tax'), value: classroom.tax },
          { label: i18next.t('order.total'), value: getSubtotal(classroom.products) + classroom.postage + classroom.tax },
        ].map((item) => (
          <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: item.label === i18next.t('order.total') ? 700 : 400 }}>
              {i18next.t('common.price_yen', { price: item.value })}
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 1 : 3 }}>
      <Grid container spacing={isMobile ? 1.5 : 3}>
        <Grid item xs={12}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ mb: isMobile ? 2 : 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => navigate('/orders')}
                sx={{ bgcolor: '#f5f5f7', '&:hover': { bgcolor: '#e8e8e8' } }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant={isMobile ? 'h6' : 'h5'}>
                {i18next.t('order.order_details')}
              </Typography>
            </Box>

            <Grid container spacing={isMobile ? 1 : 3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Box sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    height: '36px'
                  }}>
                    <Typography color="textSecondary" sx={{ width: '50%', fontSize: isMobile ? '0.85rem' : undefined }}>
                      {i18next.t('order.order_number')}
                    </Typography>
                    <Box sx={{ width: '50%', textAlign: 'right' }}>
                      <Typography sx={{ fontSize: isMobile ? '0.85rem' : undefined }}>{order.id}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    height: '36px'
                  }}>
                    <Typography color="textSecondary" sx={{ width: '50%', fontSize: isMobile ? '0.85rem' : undefined }}>
                      {i18next.t('order.orderer_name')}
                    </Typography>
                    <Box sx={{ width: '50%', textAlign: 'right' }}>
                      <Typography sx={{ fontSize: isMobile ? '0.85rem' : undefined }}>{order.requestCompany}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    height: '36px'
                  }}>
                    <Typography color="textSecondary" sx={{ width: '50%', fontSize: isMobile ? '0.85rem' : undefined }}>
                      {i18next.t('invoice.total_amount')}
                    </Typography>
                    <Box sx={{ width: '50%', textAlign: 'right' }}>
                      <Typography sx={{ fontSize: isMobile ? '0.85rem' : undefined }}>
                        {i18next.t('common.price_yen', { price: order.totalAmount })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Box sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    height: '36px'
                  }}>
                    <Typography color="textSecondary" sx={{ width: '50%', fontSize: isMobile ? '0.85rem' : undefined }}>
                      {i18next.t('common.status')}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '32px',
                      width: '50%',
                      justifyContent: 'flex-end'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                            label={getStatusText(order.status) + (order.mixed ? ' (混)' : '')}
                            sx={getStatusStyle(order.status)}
                            size="small"
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    height: '36px'
                  }}>
                    <Typography color="textSecondary" sx={{ width: '50%', fontSize: isMobile ? '0.85rem' : undefined }}>
                      {i18next.t('order.orderer_id')}
                    </Typography>
                    <Box sx={{ width: '50%', textAlign: 'right' }}>
                      <Typography sx={{ fontSize: isMobile ? '0.85rem' : undefined }}>{order.requesterId}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    height: '36px'
                  }}>
                    <Typography color="textSecondary" sx={{ width: '50%', fontSize: isMobile ? '0.85rem' : undefined }}>
                      {i18next.t('order.order_date')}
                    </Typography>
                    <Box sx={{ width: '50%', textAlign: 'right' }}>
                      <Typography sx={{ fontSize: isMobile ? '0.85rem' : undefined }}>{formatDate(order.createdAt)}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: isMobile ? 3 : 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {i18next.t('order.order_list')}
              </Typography>

              {isMobile ? (
                /* モバイル: カードビュー */
                <Box>
                  {order.classrooms.map((classroom, index) => renderMobileClassroom(classroom, index))}
                </Box>
              ) : (
                /* デスクトップ: テーブルビュー */
                <Box sx={{
                  mt: '20px'
                }}>
                  <Paper sx={{
                    boxShadow: 'none',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                  }}>
                    {order.classrooms.map((classroom, index) => (
                        <Box key={classroom.id} sx={{ mb: 4 }}>
                          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <Table sx={{
                              '& .MuiTableCell-root': {
                                borderRight: '1px solid #f0f0f0',
                                borderBottom: '1px solid #f0f0f0',
                                padding: '0 8px',
                                height: '40px',
                                whiteSpace: 'nowrap'
                              }
                            }} size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    width: '3% !important',
                                    textAlign: 'center',
                                    height: '40px !important'
                                  }}>
                                    {i18next.t('common.number')}
                                  </TableCell>
                                  <TableCell sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    width: '200px',
                                    textAlign: 'center',
                                    height: '40px !important'
                                  }}>
                                    {i18next.t('order.classroom')}
                                  </TableCell>
                                  <TableCell sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    width: '300px',
                                    textAlign: 'center',
                                    height: '40px !important'
                                  }}>
                                    {i18next.t('address.address')}
                                  </TableCell>
                                  <TableCell sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    width: '130px',
                                    textAlign: 'center',
                                    height: '40px !important'
                                  }}>
                                    {i18next.t('order.delivery_date')}
                                  </TableCell>
                                  <TableCell sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    width: '130px',
                                    textAlign: 'center',
                                    height: '40px !important'
                                  }}>
                                    {i18next.t('order.time_slot')}
                                  </TableCell>
                                  <TableCell sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    width: '130px',
                                    textAlign: 'center',
                                    height: '40px !important'
                                  }}>
                                    {i18next.t('common.status')}
                                  </TableCell>
                                  <TableCell sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    width: '200px',
                                    textAlign: 'center',
                                    height: '40px !important'
                                  }}>
                                    {i18next.t('order.tracking_number')}
                                  </TableCell>
                                  {authStore.user.level === 'ADMIN' && (
                                    <TableCell sx={{
                                      backgroundColor: '#f5f5f5',
                                      fontSize: '0.7rem',
                                      fontWeight: 800,
                                      width: '130px',
                                      textAlign: 'center',
                                      height: '40px !important'
                                    }}>
                                      {i18next.t('order.delivery_slip_number')}
                                    </TableCell>
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {index + 1}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {classroom.classroomName}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>
                                    {classroom.address}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {formatDate(classroom.deliveryDate)}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {getTimeSlot(classroom.deliveryTime)}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                      {getStatusText(classroom.status)}
                                      {(authStore.user.level === 'ADMIN' && (!classroom.invoiceId)) &&
                                        <IconButton
                                          size="small"
                                          onClick={() => openClassroomStatusDialog(classroom)}
                                          sx={{ padding: '4px' }}
                                        >
                                          <EditIcon fontSize="small" style={{width: 14, marginTop: -3}}/>
                                        </IconButton>
                                      }
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {editingTrackingNumber === classroom.id ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <TextField
                                          size="small"
                                          value={editTrackingNumberValue}
                                          onChange={(e) => setEditTrackingNumberValue(e.target.value)}
                                          sx={{
                                            mr: 1,
                                            '& .MuiInputBase-root': {
                                              height: '32px',
                                              fontSize: '0.7rem'
                                            }
                                          }}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={handleCancelEditTrackingNumber}
                                          sx={{ padding: '4px', mr: 0.5 }}
                                        >
                                          <CloseIcon fontSize="small" style={{width: 14, marginTop: -3}}/>
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleSaveTrackingNumber(classroom)}
                                          sx={{ padding: '4px' }}
                                        >
                                          <SaveIcon fontSize="small" style={{width: 14, marginTop: -3}}/>
                                        </IconButton>
                                      </Box>
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {classroom.trackingNumber}
                                        {authStore.user.level === 'ADMIN' && ['CONFIRM', 'SHIPPING_START'].includes(classroom.status) &&
                                          <IconButton
                                            size="small"
                                            disabled={classroom.status === 'CONFIRM'}
                                            onClick={() => handleStartEditTrackingNumber(classroom)}
                                            sx={{ padding: '4px', ml: 1, opacity: classroom.status === 'CONFIRM' ? 0.3 : 1 }}
                                          >
                                            <EditIcon fontSize="small" style={{width: 14, marginTop: -3}}/>
                                          </IconButton>
                                        }
                                      </Box>
                                    )}
                                  </TableCell>
                                  {authStore.user.level === 'ADMIN' && (
                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                      {classroom.orderNumber}
                                    </TableCell>
                                  )}
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={authStore.user.level === 'ADMIN' ? 8 : 7} sx={{ height: '16px !important', borderRight: 'none !important', borderBottom: 'none !important' }} />
                                </TableRow>
                                <TableRow>
                                  <TableCell /><TableCell />
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {i18next.t('order.textbook_name')}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {i18next.t('product.price')}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {i18next.t('order.quantity')}
                                  </TableCell>
                                  <TableCell />
                                  <TableCell />
                                  {authStore.user.level === 'ADMIN' && <TableCell />}
                                </TableRow>
                                {classroom.products.map((product, index) => (
                                    <TableRow key={`${product.orderClassroomId}-${product.productType}-${product.productId}`}>
                                      <TableCell />
                                      <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                        {index + 1}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem' }}>
                                        {product.productTitle}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem', textAlign: 'right', pr: 2 }}>
                                        {i18next.t('common.price_yen', { price: product.productPrice.toLocaleString() })}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                        {product.quantity}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem', textAlign: 'right', pr: 2 }}>
                                        {i18next.t('common.price_yen', { price: (product.productPrice * product.quantity).toLocaleString() })}
                                      </TableCell>
                                      <TableCell />
                                      {authStore.user.level === 'ADMIN' && <TableCell />}
                                    </TableRow>
                                ))}
                                <TableRow>
                                  <TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
                                  {authStore.user.level === 'ADMIN' && <TableCell />}
                                </TableRow>
                                <TableRow>
                                  <TableCell /><TableCell /><TableCell /><TableCell />
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {i18next.t('order.subtotal')}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'right', pr: 2 }}>
                                    {i18next.t('common.price_yen', { price: getSubtotal(classroom.products).toLocaleString()})}
                                  </TableCell>
                                  <TableCell />
                                  {authStore.user.level === 'ADMIN' && <TableCell />}
                                </TableRow>
                                <TableRow>
                                  <TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
                                  {authStore.user.level === 'ADMIN' && <TableCell />}
                                </TableRow>
                                <TableRow>
                                  <TableCell /><TableCell /><TableCell /><TableCell />
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {i18next.t('user.shipping_fee')}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'right', pr: 2 }}>
                                    {i18next.t('common.price_yen', { price: classroom.postage.toLocaleString() })}
                                  </TableCell>
                                  <TableCell />
                                  {authStore.user.level === 'ADMIN' && <TableCell />}
                                </TableRow>
                                <TableRow>
                                  <TableCell /><TableCell /><TableCell /><TableCell />
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                    {i18next.t('order.tax')}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'right', pr: 2 }}>
                                    {i18next.t('common.price_yen', { price: classroom.tax.toLocaleString() })}
                                  </TableCell>
                                  <TableCell />
                                  {authStore.user.level === 'ADMIN' && <TableCell />}
                                </TableRow>
                                <TableRow>
                                  <TableCell /><TableCell /><TableCell /><TableCell />
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', fontWeight: 'bold' }}>
                                    {i18next.t('order.total')}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', textAlign: 'right', pr: 2, fontWeight: 'bold' }}>
                                    {i18next.t('common.price_yen', { price: (getSubtotal(classroom.products) + classroom.postage + classroom.tax).toLocaleString()})}
                                  </TableCell>
                                  <TableCell />
                                  {authStore.user.level === 'ADMIN' && <TableCell />}
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                    ))}
                  </Paper>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/*<Dialog*/}
      {/*  open={statusDialogOpen}*/}
      {/*  onClose={() => setStatusDialogOpen(false)}*/}
      {/*  maxWidth="xs"*/}
      {/*  fullWidth*/}
      {/*>*/}
      {/*  <DialogTitle>{i18next.t('user.change_status')}</DialogTitle>*/}
      {/*  <DialogContent>*/}
      {/*    <FormControl fullWidth>*/}
      {/*      <Select*/}
      {/*        autoFocus*/}
      {/*        margin="dense"*/}
      {/*        value={selectedStatus}*/}
      {/*        onChange={(e) => setSelectedStatus(e.target.value)}*/}
      {/*        sx={{ mt: 1 }}*/}
      {/*      >*/}
      {/*        {getStatusOptions().map((option) => (*/}
      {/*          <MenuItem key={option.value} value={option.value}>*/}
      {/*            {option.label}*/}
      {/*          </MenuItem>*/}
      {/*        ))}*/}
      {/*      </Select>*/}
      {/*    </FormControl>*/}
      {/*  </DialogContent>*/}
      {/*  <DialogActions>*/}
      {/*    <Button onClick={() => setStatusDialogOpen(false)}>*/}
      {/*      {i18next.t('common.cancel')}*/}
      {/*    </Button>*/}
      {/*    <Button onClick={handleStatusChange} variant="contained" color="primary">*/}
      {/*      {i18next.t('common.save')}*/}
      {/*    </Button>*/}
      {/*  </DialogActions>*/}
      {/*</Dialog>*/}

      <Dialog
        open={classroomStatusDialogOpen}
        onClose={() => setClassroomStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{i18next.t('user.change_status')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <Select
              autoFocus
              margin="dense"
              value={selectedClassroomStatus}
              onChange={(e) => {
                setSelectedClassroomStatus(e.target.value);
                // SHIPPING_COMPLETEに変更した時、日付が未設定なら今日をデフォルトに
                if (e.target.value === 'SHIPPING_COMPLETE' && !shippingCompletedDate) {
                  setShippingCompletedDate(new Date().toISOString().substring(0, 10));
                }
              }}
              sx={{ mt: 1 }}
            >
              {getClassroomStatusOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedClassroomStatus === 'SHIPPING_START' && (
            <TextField
              label={i18next.t('order.delivery_slip_number')}
              value={orderNumberInput}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                setOrderNumberInput(v);
                setOrderNumberError('');
              }}
              error={!!orderNumberError}
              helperText={orderNumberError}
              fullWidth
              margin="normal"
              placeholder="5〜6桁の数字"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            />
          )}
          {selectedClassroomStatus === 'SHIPPING_COMPLETE' && (
            <TextField
              type="date"
              label={i18next.t('order.shipping_completed_date')}
              value={shippingCompletedDate}
              onChange={(e) => setShippingCompletedDate(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setClassroomStatusDialogOpen(false); setShippingCompletedDate(''); }}>
            {i18next.t('common.cancel')}
          </Button>
          <Button onClick={handleClassroomStatusChange} variant="contained" color="primary">
            {i18next.t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderDetail;